<template>
	<div class="rounded border border-collapse mt-4 border-ihh-gray/50 text-base max-w-2xl relative">
		<!-- Payment Not Required Message -->
		<div v-if="paymentNotRequired" class="absolute inset-0 flex items-center justify-center z-10">
			<div class="bg-ihh-light-salmon px-6 py-3 rounded-md text-ihh-midnight-gray font-semibold text-lg">Payment Not Required</div>
		</div>
		<div id="payment-table" class="text-ihh-midnight-gray w-full" :class="[paymentNotRequired ? 'opacity-20 pointer-events-none' : '']">
			<!-- Cards -->
			<PaymentOption :selectedOptionId="selectedPayment" :option="options.cards" @selected="updateActiveOption">
				<template v-slot:content>
					<Cards />
				</template>
			</PaymentOption>

			<!-- Digital Wallets -->
			<!-- <PaymentOption :selectedOptionId="selectedPayment" :option="options.digitalWallets" @selected="updateActiveOption">
				<template v-slot:content></template>
			</PaymentOption> -->

			<!-- ACH -->
			<PaymentOption v-if="store?.user?.id" :selectedOptionId="selectedPayment" :option="options.ach" @selected="updateActiveOption">
				<template v-slot:content>
					<ACH />
				</template>
			</PaymentOption>

			<!-- Net 30 -->
			<PaymentOption :selectedOptionId="selectedPayment" :option="options.net30" @selected="updateActiveOption" />
		</div>
	</div>

	<!-- Terms of Service Modal -->
	<CheckoutTerms v-model:termsAccepted="termsAccepted" />

	<!-- Checkout Button -->
	<div class="flex w-full justify-center">
		<button
			@click="handleOrderButtonClick"
			id="place-order-button"
			:class="{ 'opacity-40 pointer-events-none': orderButtonDisabled }"
			class="flex w-full px-4 py-3 justify-center text-center text-lg md:text-xl bg-ihh-dark-salmon hover:bg-ihh-dark-salmon/80 font-semibold text-ihh-midnight-gray hover:text-ihh-midnight-gray rounded uppercase"
		>
			{{ orderButtonText }}
		</button>
	</div>
</template>

<script>
// Components
import CheckoutTerms from './checkout-terms.vue'
import PaymentOption from './payment-option.vue'
import Cards from './cards.vue'
import ACH from './ach.vue'

// Helpers
import helpers from '../../helpers/checkout.js'
import Swal from 'sweetalert2'
import Braintree from '../../helpers/braintree.js'
import { useCheckoutStore } from '../../stores/store.js'
import * as Sentry from '@sentry/vue'

export default {
	name: 'PaymentTable',
	inject: ['store'],
	props: {
		isDigitalOnly: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		CheckoutTerms,
		PaymentOption,
		Cards,
		ACH,
	},
	emits: ['placingOrder', 'jump-to-dw'],
	props: {
		paymentNotRequired: {
			type: Boolean,
			default: false,
		},
		isDigitalOnly: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			checkout: useCheckoutStore(),
			processing: false,
			termsAccepted: false,
			showGooglePay: false,
			showApplePay: false,
			selectedPayment: 'cards',
			options: {
				cards: {
					id: 'cards',
					img: null,
					method: 'Credit or Debit Card',
					buttonText: 'Place Order',
					creditCards: true,
					hasContent: true,
				},
				digitalWallets: {
					id: 'digitalWallets',
					img: null,
					method: 'Digital Wallets',
					buttonText: 'Place Order',
					creditCards: false,
					hasContent: false,
				},
				ach: {
					id: 'ach',
					img: '/images/ach-edited.png',
					method: 'US Bank Account',
					buttonText: 'Place Order',
					creditCards: false,
					hasContent: true,
				},
				net30: {
					id: 'net30',
					img: null,
					method: 'NET 30 (FOR AUTHORIZED CUSTOMERS ONLY)',
					buttonText: 'Place Order',
					creditCards: false,
					hasContent: false,
				},
			},
		}
	},
	computed: {
		orderButtonText() {
			return this.options[this.selectedPayment]?.buttonText || 'Place Order'
		},
		orderButtonDisabled() {
			if (!this.termsAccepted || this.processing) {
				return true
			}
			return false
		},
		checkoutValidated() {
			if (!this.checkout.emailValidated) {
				return { value: false, message: 'Missing email address' }
			}

			if (!this.isDigitalOnly) {
				if (!this.checkout.shippingAddressValidated.value) {
					return { value: false, message: this.checkout.shippingAddressValidated.message }
				}
				if (!this.checkout.shippingMethodSelected) {
					return { value: false, message: 'No shipping method selected' }
				}
			}
			if (!this.checkout.billingAddressValidated.value) {
				return { value: false, message: this.checkout.billingAddressValidated.message }
			}

			if (!this.checkout.paymentNotRequired) {
				if (this.selectedPayment === 'cards') {
					if (this.checkout.payment.addingNewCard) {
						if (!this.newCardValidated.value) {
							return { value: false, message: this.newCardValidated.message }
						}
					} else {
						if (!this.storedCardValidated.value) {
							return { value: false, message: this.storedCardValidated.message }
						}
					}
				}

				if (this.selectedPayment === 'ach') {
					if (this.checkout.payment.addingNewACH) {
						if (!this.newACHValidated.value) {
							return { value: false, message: this.newACHValidated.message }
						}
					} else {
						if (!this.storedACHValidated.value) {
							return { value: false, message: this.storedACHValidated.message }
						}
					}
				}
			}

			return { value: true }
		},
		newCardValidated() {
			const missingFields = []
			if (!this.checkout.payment.newCard?.name) missingFields.push('Name on card')
			if (!this.checkout.payment.newCard?.number) missingFields.push('Card number')
			if (!this.checkout.payment.newCard?.expiry) missingFields.push('Expiration date')
			if (!this.checkout.payment.newCard?.cvv) missingFields.push('CVV')

			if (missingFields.length > 0) {
				return {
					value: false,
					message: `Missing card information: ${missingFields.join(', ')}`,
				}
			}
			return { value: true }
		},
		storedCardValidated() {
			const missingFields = []
			if (!this.checkout.payment.selectedCard?.last4 || !this.checkout.payment.selectedCard?.token) missingFields.push('Selected card')
			if (!this.checkout.payment.cardCVV) missingFields.push('CVV')

			if (missingFields.length > 0) {
				return {
					value: false,
					message: `Missing stored card information: ${missingFields.join(', ')}`,
				}
			}
			return { value: true }
		},
		newACHValidated() {
			const ownershipType = this.checkout.payment.newACH?.ownershipType
			const missingFields = []

			// Check base fields required for all ACH payments
			if (!this.checkout.payment.newACH?.accountNumber) missingFields.push('Account number')
			if (!this.checkout.payment.newACH?.routingNumber) missingFields.push('Routing number')
			if (!ownershipType) missingFields.push('Ownership type')

			// Check fields specific to ownership type
			if (ownershipType === 'personal') {
				if (!this.checkout.payment.newACH?.firstName) missingFields.push('First name')
				if (!this.checkout.payment.newACH?.lastName) missingFields.push('Last name')
			} else if (ownershipType === 'business') {
				if (!this.checkout.payment.newACH?.businessName) missingFields.push('Business name')
			}

			if (missingFields.length > 0) {
				return {
					value: false,
					message: `Missing ACH information: ${missingFields.join(', ')}`,
				}
			}
			return { value: true }
		},
		storedACHValidated() {
			const missingFields = []
			const selectedACH = this.checkout.payment.selectedACH || {}
			const ownershipType = selectedACH.ownershipType

			// Check token for all account types
			if (!selectedACH.token) missingFields.push('Stored account')

			// Check name fields based on ownership type
			if (ownershipType === 'business') {
				if (!selectedACH.businessName) missingFields.push('Business name')
			} else {
				// Default to personal account validation if ownership type is missing or is 'personal'
				if (!selectedACH.firstName) missingFields.push('First name')
				if (!selectedACH.lastName) missingFields.push('Last name')
			}

			if (missingFields.length > 0) {
				return {
					value: false,
					message: `Missing stored ACH information: ${missingFields.join(', ')}`,
				}
			}
			return { value: true }
		},
	},

	methods: {
		handleOrderButtonClick() {
			if (this.processing) return
			this.processing = true
			console.log('handleOrderButton: is checkout validated: ', this.checkoutValidated?.value)
			if (this.checkoutValidated?.value) {
				console.log('placing order via process payment')
				this.processPayment()
			} else {
				this.processing = false
				this.logToSentry(this.checkoutValidated?.message, 'missing required fields')
				Swal.fire({
					icon: 'warning',
					title: 'Missing Required Fields',
					text: this.checkoutValidated?.message,
					confirmButtonText: 'OK',
					confirmButtonColor: '#e59789',
				})
			}
		},

		updateActiveOption(id) {
			this.selectedPayment = id
			if (this.selectedPayment === 'digitalWallets') {
				this.$emit('jump-to-dw')
				console.log('Emmitting jumpToDW')
			}
		},

		/**
		 * Route the payment processing based on selected payment method
		 */
		async processPayment() {
			if (this.paymentNotRequired) {
				this.checkout.updateShowOverlay(true)
				const validateCheckout = helpers.validateCheckoutInfo('apm')
				if (!validateCheckout.success) {
					this.processing = false
					return Swal.fire({
						icon: 'error',
						title: 'Missing Required Fields',
						text: validateCheckout?.message,
						confirmButtonText: 'OK',
						confirmButtonColor: '#e59789',
					})
				}

				const orderResponse = await helpers.placeOrder({ paymentMethod: 'apm' })

				if (!orderResponse?.success) {
					this.processing = false
					return Swal.fire({
						icon: 'error',
						title: 'Error',
						text: orderResponse?.message,
						confirmButtonText: 'OK',
					})
				}

				const successResponse = await helpers.handleCheckoutSuccess(orderResponse?.bcOrderId, { data: { status: 'settled' } }, ['settled'], 'apm', false)
				this.$router.push(successResponse?.redirectUrl)
				return
			}

			const validateConsignment = await helpers.validateConsignment()
			if (!validateConsignment) {
				this.processing = false
				this.logToSentry({ success: false, error: 'Missing consignment' }, 'Missing consignment')
				this.checkout.resetShippingAddress()
				this.checkout.resetBillingAddress()
				Swal.fire({
					icon: 'warning',
					title: 'Shipping Error',
					text: "🚧 Oh no! We've lost track of your shipping details; please re-enter and we'll get you on your way!",
					confirmButtonText: 'OK',
					confirmButtonColor: '#e59789',
				})
				return
			}

			switch (this.selectedPayment) {
				case 'cards':
					return await this.processCardPayment()
				case 'ach':
					return await this.processACHPayment()
				case 'net30':
					return await this.processNet30()
				default:
					console.error('No payment method selected')
			}
		},

		/**
		 * Process NET 30 payment for authorized customers
		 */
		async processNet30() {
			// Swal alert for ACH mandate authorization
			const { value: approved } = await Swal.fire({
				title: 'NET 30 Payment Details',
				html: `
					<div class="bg-ihh-warning text-ihh-warning-text p-3 mt-2 rounded-lg text-sm">
						The NET 30 payment option is available exclusively to established customers who have been pre-approved. If you are not currently approved, please choose an alternative payment option at checkout. To request NET 30 terms, please contact us.
					</div>
				`,
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Continue',
				cancelButtonText: 'Cancel',
			})

			if (!approved) return

			this.checkout.updateShowOverlay(true)
			const checkout = await helpers.handleCheckout(this.selectedPayment)

			console.log('checkout response: ', checkout)

			if (!checkout?.success) {
				this.checkout.updateShowOverlay(false)
				return Swal.fire({
					icon: 'error',
					title: checkout?.message || 'Error',
					text: checkout?.error || 'Unexpected error processing checkout. Please try again or contact support.',
					confirmButtonText: 'OK',
				})
			}

			console.log('checkout: ', checkout)
			this.checkout.updateShowOverlay(false)
			this.$router.push(checkout?.redirectUrl)
		},
		async processCardPayment() {
			this.checkout.updateShowOverlay(true)
			const checkout = await helpers.handleCheckout(this.selectedPayment)

			console.log('checkout response: ', checkout)

			if (!checkout?.success) {
				this.checkout.updateShowOverlay(false)
				this.processing = false
				Sentry.withScope((scope) => {
					scope.setTag('mangoCart', this.store?.cart?.id || 'mangoCart not found')
					scope.setTag('guest', this.store?.user?.id ? 'false' : 'true')
					scope.setTag('User', this.store?.user?.email || this.checkout?.guest?.email || 'User not found')
					scope.setTag('bcCart', this.store?.cart?.bcId || 'bcCart not found')
					scope.setTag('component', 'PaymentTable')
					scope.setExtra('shippingAddress', this.checkout.shipping?.shippingAddress || 'shippingAddress is empty')
					scope.setExtra('billingAddress', this.checkout.billing?.billingAddress || 'billingAddress is empty')
					scope.setExtra('selectedShipping', this.checkout.shipping?.selectedShipping || 'selectedShipping is empty')
					scope.setExtra('action', 'payment-table_processCardPayment')
					scope.setExtra('response', checkout)
					scope.setExtra('newCardLength', this.checkout.payment.newCard?.number?.length)
					scope.setLevel('error')
					Sentry.captureMessage('PaymentTable: processCardPayment failed')
				})
				return Swal.fire({
					icon: 'error',
					title: checkout?.message || 'Error',
					text: checkout?.error || 'Unexpected error processing checkout. Please try again or contact support.',
					confirmButtonText: 'OK',
				})
			}

			console.log('checkout: ', checkout)
			this.checkout.updateShowOverlay(false)
			this.processing = false
			this.$router.push(checkout?.redirectUrl)
		},

		async processACHPayment() {
			const mandateText =
				'By clicking "Continue", I authorize Braintree, a service of PayPal, on behalf of In His Hands Birth Supply, to (i) verify my bank account information using bank information and consumer reports, (ii) debit my bank account, and (iii) securely store my bank account information for future transactions.'

			// Swal alert for ACH mandate authorization
			const { value: approved } = await Swal.fire({
				title: 'ACH Mandate Authorization',
				html: `
					<p class="text-left mb-4">${mandateText}</p>
					<div class="bg-ihh-warning text-ihh-warning-text p-3 mt-4 rounded-lg text-sm">
						Please allow 2–4 business days for ACH payments to process. Your order will be shipped once the transfer has fully cleared. Thank you!
					</div>
				`,
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Continue',
				cancelButtonText: 'Cancel',
			})

			if (!approved) {
				this.processing = false
				return
			}

			this.checkout.updateShowOverlay(true)

			let braintreeClientToken, nonce, deviceData, formattedBillingAddress

			try {
				// Vault new bank account and get payment nonce
				if (this.checkout.payment.addingNewACH) {
					// Get the client token
					const fetchClientToken = await Braintree.getClientToken(this.store.api)
					braintreeClientToken = fetchClientToken.token

					// Request payment nonce
					console.log('Initiating ACH payment for new account...')
					const initiateACH = await Braintree.initiateACHCheckout({
						clientToken: braintreeClientToken,
						billingAddress: this.checkout.billing.billingAddress,
						newAccount: this.checkout.payment.newACH,
						mandateText,
					})

					console.log('ACH Initiation Response: ', initiateACH?.success)
					if (!initiateACH?.success) {
						this.checkout.updateShowOverlay(false)
						this.processing = false
						this.logToSentry(initiateACH)
						return Swal.fire({
							icon: 'error',
							title: 'Error initiating ACH payment',
							text: initiateACH?.error,
						})
					}

					deviceData = initiateACH.deviceData
					nonce = initiateACH.nonce
					formattedBillingAddress = initiateACH.billingAddress
				}

				if (this.checkout.payment.addingNewACH && !nonce) {
					this.checkout.updateShowOverlay(false)
					this.processing = false
					this.logToSentry({
						success: false,
						error: 'Error processing payment (missing payment nonce)',
					})
					return Swal.fire({
						icon: 'error',
						title: 'Error initiating ACH payment',
						text: 'Error processing payment (missing payment nonce)',
					})
				}

				// Create the Big Commerce order
				const createBCOrder = await helpers.placeOrder({ paymentMethod: 'apm' })
				console.log('after createBCOrder')
				if (!createBCOrder?.success) {
					console.log('createBCOrder failed')
					console.log('Before emit placingOrder(false)')
					this.checkout.updateShowOverlay(false)
					this.processing = false
					this.logToSentry(createBCOrder)
					console.log('Emitted placingOrder(false)')

					return Swal.fire({
						icon: 'error',
						title: 'Error Creating Order',
						text: createBCOrder?.message || 'There was an error creating your order. Please try again later.',
						confirmButtonText: 'OK',
					})
				}

				console.log('response from createBCorder', createBCOrder)
				const bcOrderId = createBCOrder?.bcOrderId
				this.checkout.setBcOrderId(bcOrderId)

				console.log('BC Order ID:', bcOrderId)

				// Define process payment data
				const processPaymentData = {
					api: this.store.api,
					amount: this.checkout.remainingBalance,
					bcOrderId,
					billingAddress: formattedBillingAddress,
					saveAccount: true, // this.checkout.payment.saveNewACH, // account must be vaulted to process via API
				}

				if (this.checkout.payment.addingNewACH) {
					processPaymentData.accountHolder = {
						firstName: this.checkout.payment.newACH.firstName,
						lastName: this.checkout.payment.newACH.lastName,
						email: this.store?.user?.email || this.guest.email,
					}
					processPaymentData.nonce = nonce
					processPaymentData.deviceData = deviceData
				} else {
					processPaymentData.accountHolder = {
						firstName: this.checkout.payment.selectedACH.firstName,
						lastName: this.checkout.payment.selectedACH.lastName,
						email: this.store?.user?.email || this.guest.email,
					}
					processPaymentData.token = this.checkout.payment.selectedACH.token
				}

				// Process vaulted account (existing or newly vaulted)
				console.log('Calling Mango to process ACH nonce....')
				const processPayment = await Braintree.processACHPayment(processPaymentData)

				if (!processPayment?.success) {
					this.checkout.updateShowOverlay(false)
					this.processing = false
					this.logToSentry(processPayment)
					console.log('Error processing ACH payment: ', processPayment?.message)
					return Swal.fire({
						icon: 'error',
						title: 'Error Processing Payment',
						html: processPayment?.message || 'There was an error processing your ACH payment. Please try again later.',
						confirmButtonText: 'OK',
					})
				}

				console.log('Transaction ID:', processPayment?.data?.id)
				console.log('Order ID:', processPayment?.data?.orderId)
				console.log('Status:', processPayment?.data?.status)

				const handleCheckout = await helpers.handleCheckoutSuccess(bcOrderId, processPayment, ['settlement_pending', 'settling', 'settled'], 'US Bank Account')

				this.checkout.updateShowOverlay(false)
				this.processing = false
				this.$router.push(handleCheckout?.redirectUrl)
			} catch (error) {
				this.checkout.updateShowOverlay(false)
				this.processing = false
				console.log('Error caught processing ACH payment: ', error)
				this.logToSentry(error)
				return Swal.fire({
					icon: 'error',
					title: 'Error Processing Payment',
					text: 'There was an error processing your ACH payment. Please try again later.',
					confirmButtonText: 'OK',
				})
			}
		},

		logToSentry(response, message) {
			Sentry.withScope((scope) => {
				scope.setTag('mangoCart', this.store?.cart?.id || 'mangoCart not found')
				scope.setTag('guest', this.store?.user?.id ? 'false' : 'true')
				scope.setTag('User', this.store?.user?.email || this.checkout?.guest?.email || 'User not found')
				scope.setTag('bcCart', this.store?.cart?.bcId || 'bcCart not found')
				scope.setTag('component', 'PaymentTable')
				scope.setExtra('shippingAddress', this.checkout.shipping?.shippingAddress || 'shippingAddress is empty')
				scope.setExtra('billingAddress', this.checkout.billing?.billingAddress || 'billingAddress is empty')
				scope.setExtra('selectedShipping', this.checkout.shipping?.selectedShipping || 'selectedShipping is empty')
				scope.setExtra('mangoCartID', this.store?.cart?.id || this.store?.cart?._id || 'mangoCart not found')
				scope.setTag('action', 'processPayment')
				scope.setExtra('response', response || 'no response provided')
				scope.setLevel('error')
				Sentry.captureMessage(`PaymentTable: ${message || 'processPayment failed'}`)
			})
		},
	},
}
</script>

<style scoped></style>
